<template>
  <div class="position-relative">
    <div
      v-if="responsive"
      class="table-responsive"
    >
      <table :class="tableClasses">
        <thead v-if="header">
          <tr>
            <th
              v-for="(field, idx) in columns"
              :key="idx"
              :class="field._classes"
              :style="{ ...normalizeStyle(field._style), cursor: field.sorter !== false && sortable ? 'pointer' : undefined }"
              @click="field.sorter !== false && sortable ? toggleSort(field.key) : null"
            >
              <slot
                :name="field.key + '-header'"
                :column="field"
              >
                {{ field.label || field.key }}
              </slot>
              <span
                v-if="sortable && sorterColumn === field.key"
                class="ms-1"
              >
                {{ sorterAsc ? '▲' : '▼' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in paginatedItems"
            :key="itemKey ? item[itemKey] : index"
            :class="item._classes"
            @click="$emit('row-clicked', item, index, $event)"
          >
            <template
              v-for="(field, idx) in columns"
              :key="idx"
            >
              <slot
                v-if="$slots[field.key]"
                :name="field.key"
                :item="item"
                :index="index"
              />
              <td
                v-else
                :class="item._cellClasses && item._cellClasses[field.key]"
                v-bind="item._cellProps && item._cellProps[field.key]"
              >
                {{ getValue(item, field.key) }}
              </td>
            </template>
          </tr>
          <tr v-if="!items.length && !loading">
            <td
              :colspan="columns.length"
              class="text-center py-4"
            >
              <slot name="no-items-view">
                {{ noItemsText }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-else
      class="position-relative"
    >
      <table :class="tableClasses">
        <thead v-if="header">
          <tr>
            <th
              v-for="(field, idx) in columns"
              :key="idx"
              :class="field._classes"
              :style="{ ...normalizeStyle(field._style), cursor: field.sorter !== false && sortable ? 'pointer' : undefined }"
              @click="field.sorter !== false && sortable ? toggleSort(field.key) : null"
            >
              <slot
                :name="field.key + '-header'"
                :column="field"
              >
                {{ field.label || field.key }}
              </slot>
              <span
                v-if="sortable && sorterColumn === field.key"
                class="ms-1"
              >
                {{ sorterAsc ? '▲' : '▼' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in paginatedItems"
            :key="itemKey ? item[itemKey] : index"
            :class="item._classes"
          >
            <template
              v-for="(field, idx) in columns"
              :key="idx"
            >
              <slot
                v-if="$slots[field.key]"
                :name="field.key"
                :item="item"
                :index="index"
              />
              <td
                v-else
                :class="item._cellClasses && item._cellClasses[field.key]"
              >
                {{ getValue(item, field.key) }}
              </td>
            </template>
          </tr>
          <tr v-if="!items.length && !loading">
            <td
              :colspan="columns.length"
              class="text-center py-4"
            >
              <slot name="no-items-view">
                {{ noItemsText }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="paginationEnabled"
      class="d-flex justify-content-center mt-2"
    >
      <ul class="pagination mb-0">
        <li
          class="page-item"
          :class="{ disabled: currentPage <= 1 }"
        >
          <a
            class="page-link"
            href="#"
            @click.prevent="goToPage(currentPage - 1)"
          >‹</a>
        </li>
        <li
          v-for="page in totalPages"
          :key="page"
          class="page-item"
          :class="{ active: page === currentPage }"
        >
          <a
            class="page-link"
            href="#"
            @click.prevent="goToPage(page)"
          >{{ page }}</a>
        </li>
        <li
          class="page-item"
          :class="{ disabled: currentPage >= totalPages }"
        >
          <a
            class="page-link"
            href="#"
            @click.prevent="goToPage(currentPage + 1)"
          >›</a>
        </li>
      </ul>
    </div>
    <CElementCover
      v-if="loading"
      :boundaries="[{ sides: ['top'], query: 'td' }, { sides: ['bottom'], query: 'tbody' }]"
    />
  </div>
</template>

<script>
import { CElementCover } from '@coreui/vue-pro'

export default {
  name: 'CDataTable',
  components: { CElementCover },
  props: {
    items: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    itemsPerPage: { type: Number, default: 10 },
    activePage: { type: Number, default: 1 },
    pagination: { type: [Boolean, Object], default: false },
    sorter: { type: [Boolean, Object], default: false },
    loading: { type: Boolean, default: false },
    responsive: { type: Boolean, default: true },
    striped: { type: Boolean, default: false },
    hover: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    border: { type: Boolean, default: false },
    dark: { type: Boolean, default: false },
    fixed: { type: Boolean, default: false },
    header: { type: Boolean, default: true },
    clickableRows: { type: Boolean, default: false },
    noItemsView: { type: Object, default: () => ({}) },
    cleaner: { type: Boolean, default: false },
    addTableClasses: { type: [String, Array, Object], default: null },
    itemKey: { type: String, default: null },
  },
  emits: ['update:sorter-value', 'row-clicked', 'page-change', 'pagination-change'],
  data() {
    return {
      currentPage: this.activePage || 1,
      sorterColumn: null,
      sorterAsc: true,
    }
  },
  computed: {
    columns() {
      return this.fields.map(f => {
        if (typeof f === 'string') return { key: f, label: f }
        return f
      })
    },
    itemKeyField() {
      return this.itemKey || null
    },
    sortable() {
      return !!this.sorter
    },
    externalSort() {
      return this.sorter && this.sorter.external === true
    },
    paginationEnabled() {
      return this.pagination === true || (typeof this.pagination === 'object' && this.pagination !== null)
    },
    sortedItems() {
      if (!this.sortable || this.externalSort) return [...this.items]
      if (!this.sorterColumn) return [...this.items]
      return [...this.items].sort((a, b) => {
        const valA = this.getValue(a, this.sorterColumn)
        const valB = this.getValue(b, this.sorterColumn)
        const aEmpty = valA === null || valA === undefined || valA === ''
        const bEmpty = valB === null || valB === undefined || valB === ''
        if (aEmpty && bEmpty) return 0
        if (aEmpty) return 1
        if (bEmpty) return -1
        if (typeof valA === 'number' || typeof valB === 'number') {
          return this.sorterAsc ? valA - valB : valB - valA
        }
        const cmp = String(valA).localeCompare(String(valB), undefined, { sensitivity: 'base', numeric: true })
        return this.sorterAsc ? cmp : -cmp
      })
    },
    totalPages() {
      if (!this.paginationEnabled) return 1
      return Math.max(1, Math.ceil(this.sortedItems.length / this.itemsPerPage))
    },
    paginatedItems() {
      if (!this.paginationEnabled) return this.sortedItems
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.sortedItems.slice(start, start + this.itemsPerPage)
    },
    tableClasses() {
      return [
        'table',
        {
          'table-striped': this.striped,
          'table-hover': this.hover,
          'table-sm': this.small,
          'table-bordered': this.border,
          'table-dark': this.dark,
        },
        this.addTableClasses,
      ]
    },
    noItemsText() {
      return (this.noItemsView && this.noItemsView.noItems) || 'No items found'
    },
  },
  watch: {
    activePage(val) {
      this.currentPage = val || 1
    },
    totalPages(val) {
      if (this.currentPage > val) this.currentPage = val
    },
  },
  methods: {
    normalizeStyle(style) {
      if (!style) return {}
      if (typeof style === 'string') {
        const obj = {}
        style.split(';').forEach(part => {
          const idx = part.indexOf(':')
          if (idx > -1) {
            const k = part.slice(0, idx).trim()
            const v = part.slice(idx + 1).trim()
            if (k && v) obj[k] = v
          }
        })
        return obj
      }
      return { ...style }
    },
    getValue(obj, path) {
      if (!path || !obj) return undefined
      return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj)
    },
    toggleSort(column) {
      if (this.sorterColumn === column) {
        this.sorterAsc = !this.sorterAsc
      } else {
        this.sorterColumn = column
        this.sorterAsc = true
      }
      this.$emit('update:sorter-value', { column: this.sorterColumn, asc: this.sorterAsc })
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.$emit('page-change', page)
    },
  },
}
</script>
